import InternalToolsService from '../../../services/InternalToolsService'
import config from '../config'
import { requests } from '.'
import { transformToNewPayload } from '../utils/LocationNumbers'
interface IGetCountryNumbers {
  fingerprintId: number | string
  numbers: any[]
}
const subdomainTwilio = '/conversations/providers/twilio'

export const LocationNumbersService = {
  getPhoneNumbers: async (locationId: string, payload: any) => {
    const { limit, page, query = '' } = payload
    try {
      const { data } = await requests(config.APP_ENGINE_API_URL).get(
        `/location/${locationId}/numbers?limit=${limit}&page=${page}&query=${query}`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getInboundPhoneNumbers: async (locationId: string, twilioAccountId: string) => {
    try {
      const { data } = await requests(config.APP_ENGINE_API_URL).get(
        `twilio/get_inbound_numbers/${twilioAccountId}?location_id=${locationId}&skipNumberPool=true`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getPhoneNumbersSID: async (locationId: string) => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).get(
        `/phone-system/twilio-accounts/numbers-sid-map?locationId=${locationId}`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getNumberPools: async (numberPools: string[]) => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).post(`/phone-system/number-pools`, {
        numberPools: numberPools,
      })
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  addNumberToPool: async (payload: {
    locationId: string;
    poolId: string;
    phoneNumber: string;
  }) => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/phone-system/number-pools/add-number`,
        payload
      )
      return data
    } catch (error) {
      throw error
    }
  },
  
  removeNumberFromPool: async (payload: {
    locationId: string;
    poolId: string;
    phoneNumber: string;
  }) => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/phone-system/number-pools/remove-number`,
        payload
      )
      return data
    } catch (error) {
      throw error
    }
  },
  
  transferNumberBetweenPools: async (payload: {
    locationId: string;
    sourcePoolId: string;
    destinationPoolId: string;
    phoneNumber: string;
  }) => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/phone-system/number-pools/transfer-number`,
        payload
      )
      return data
    } catch (error) {
      throw error
    }
  },
  
  deleteNumberFromPool: async (payload: {
    locationId: string;
    poolId: string;
    phoneNumber: string;
    numberId: string;
  }) => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/phone-system/number-pools/delete-number`,
        payload
      )
      return data
    } catch (error) {
      throw error
    }
  },

  getDetailedTwilioAccount: async (locationId: string) => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).get(
        `/phone-system/twilio-accounts?entityId=${locationId}&entityType=LOCATION`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },
  getSubAccountStatus: async (locationId: string) => {
    try {
      const { data } = await requests(config.REVEX_BACKEND_LC_PHONE_URL).get(`/sub-account/${locationId}`)
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },
  getCampaigns: async (locationId: string, companyId: string) => {
    try {
      const { data } = await requests(config.ISV).get(`/compliance/${companyId}/campaigns/${locationId}`)
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getCallerIDs: async (locationId: string) => {
    try {
      const { data } = await requests(config.ISV).get(`/caller-id/location/${locationId}`)
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getCountryNumbers: async (payload: any) => {
    const {
      locationId,
      countryCode,
      firstPart,
      lastPart,
      anywhere,
      smsEnabled,
      voiceEnabled,
      mmsEnabled,
      numberTypes,
    } = payload

    try {
      const { data }: { data: IGetCountryNumbers } = await requests(config.MARKETPLACE_REST_API_URL).get(
        `/phone-system/numbers/location/${locationId}/available`,
        {
          params: {
            countryCode,
            firstPart,
            lastPart,
            anywhere,
            smsEnabled,
            voiceEnabled,
            mmsEnabled,
            numberTypes,
          },
        }
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getLocationTwilio: async locationId => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).get(
        `${subdomainTwilio}/fetch?locationId=${locationId}`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getLocationAddresses: async (locationId, countryCode) => {
    try {
      const { data } = await requests(config.REVEX_BACKEND_LC_PHONE_URL).get(`/addresses`, {
        params: { locationId, countryCode },
      })
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getLocationBundles: async (locationId, countryCode) => {
    try {
      const { data } = await requests(config.REVEX_BACKEND_LC_PHONE_URL).get(`/addresses`, {
        params: { locationId, countryCode },
      })
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getCustomerPayment: async locationId => {
    const entity = {
      entityId: locationId,
      entityType: 'LOCATION',
    }

    try {
      const customerPaymentMethod = await InternalToolsService.getStripeCustomer(entity as any)
      return Promise.resolve(customerPaymentMethod)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  setPurchaseNumber: async (locationId: string, payload: any) => {
    const {
      addressSid,
      bundleSid,
      countryCode,
      numberType,
      phoneNumber,
      stripeAccountId,
      locality,
      region,
      paymentIntentId,
      fingerprintId,
    } = payload

    try {
      const res = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/phone-system/numbers/location/${locationId}/purchase`,
        {
          addressSid,
          bundleSid,
          countryCode,
          numberType,
          phoneNumber,
          stripeAccountId,
          locality,
          region,
          paymentIntentId,
          paymentMethodId: payload.paymentMethodId || '',
          fingerprintId,
        }
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  setPurchaseNumberWithStripe: async (locationId: string, payload: any) => {
    const {
      countryCode,
      phoneNumber,
      numberType,
      addressSid,
      bundleSid,
      stripeAccountId,
      stripeCustomerId,
      stripe,
      locality,
      region,
      paymentMethodId,
      fingerprintId,
    } = payload

    const entity = {
      entityId: locationId,
      entityType: 'LOCATION',
    }

    let lastPaymentError = null

    try {
      const stripePayload = {
        ...entity,
        amount: 1,
        stripeCustomerId: stripeCustomerId,
        stripeAccountId: stripeAccountId,
        description: 'PhoneNumberPurchase - 3DS verification',
        enable3DS: true,
      }

      const { data: createdPaymentIntent } = await requests(config.SAAS).post(
        `/saas-config/${locationId}/create-stripe-payment-intent`,
        stripePayload
      )

      if (!paymentMethodId) {
        const error = {
          response: {
            data: {
              message:
                'No default customer payment method found. Please ensure a card is added and is set as primary method for purchase',
            },
          },
        }
        return Promise.reject(error)
      }

      const confirmation = await stripe.confirmCardPayment(createdPaymentIntent?.client_secret, {
        payment_method: paymentMethodId,
      })

      lastPaymentError = confirmation?.paymentIntent?.last_payment_error

      if (!confirmation?.paymentIntent) {
        const error = {
          response: {
            data: {
              message: confirmation?.error?.message,
            },
          },
        }
        return Promise.reject(error)
      }

      const paymentIntentId = confirmation?.paymentIntent?.id

      const res = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/phone-system/numbers/location/${locationId}/purchase`,
        {
          phoneNumber: phoneNumber,
          addressSid,
          bundleSid,
          numberType,
          stripeAccountId,
          countryCode,
          locality,
          region,
          paymentIntentId,
          paymentMethodId,
          fingerprintId,
        }
      )
      return Promise.resolve(res)
    } catch (error) {
      if (lastPaymentError) error.response.data = { message: lastPaymentError }
      return Promise.reject(error)
    }
  },

  deleteNumber: async (payload: any) => {
    try {
      const { data } = await requests(config.REVEX_BACKEND_LC_PHONE_URL).delete(`/numbers/`, { data: payload })
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  deleteCallerID: async (locationId: string, sid: string) => {
    try {
      const { data } = await requests(config.ISV).delete(`/caller-id/location/${locationId}?sid=${sid}`)
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  deletePool: async (locationId: string, payload: any) => {
    try {
      const poolId = payload?.pool_id
      const encodedLocationId = encodeURIComponent(locationId)
      const encodedPoolId = encodeURIComponent(poolId)
      const queryParams = `locationId=${encodedLocationId}&poolId=${encodedPoolId}`
      const { data } = await requests(config.REVEX_BACKEND_LC_PHONE_URL).delete(
        `/number-pools/delete-pool?${queryParams}`
      )
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getRegulatoryBundles: async (payload: any) => {
    try {
      const { locationId, countryCode } = payload
      // add country code only if available
      const url = countryCode
        ? `/twilio/get_bundles/${locationId}?country_code=${countryCode}`
        : `/twilio/get_bundles/${locationId}`
      const res = await requests(config.APP_ENGINE_API_URL).get(`${url}`)
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getAllRegulatoryBundles: async (payload: any) => {
    try {
      const { locationId, countryCode } = payload
      const res = await requests(config.SAAS).get(`regulatory-bundle/${locationId}`)

      return res
    } catch (error) {
      throw error
    }
  },

  getRegulatoryAddresses: async (payload: any) => {
    try {
      const { locationId, countryCode } = payload
      const url = `/addresses`
      const res = await requests(config.REVEX_BACKEND_LC_PHONE_URL).get(url, {
        params: { locationId, countryCode },
      })
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getVerificationCall: async (payload: any) => {
    const { locationId, phoneNumber } = payload

    try {
      const res = await requests(config.ISV).post(`/caller-id/location/${locationId}/verification-code`, {
        phoneNumber,
      })
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getVerificationStatus: async (payload: any) => {
    const { locationId, phoneNumber } = payload

    try {
      const res = await requests(config.SAAS).get(`/twilio-accounts/${locationId}/${phoneNumber}`)
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getA2PBrandStatus: async (payload: any) => {
    const { companyId, locationId, brandType } = payload

    try {
      const res = await requests(config.ISV).get(
        `/compliance/${companyId}/${
          brandType + (brandType === 'standard' ? '-registration' : '')
        }/${locationId}/campaign?brandType=${brandType}`
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  updateVerifiedCallerId: async (payload: any) => {
    const { sid, friendlyName, locationId } = payload

    try {
      const res = await requests(config.ISV).put(`/caller-id/location/${locationId}`, {
        friendlyName,
        sid,
      })
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  updatePhoneNumber: async (locationId: string, payload: any) => {
    try {
      const res = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/phone-system/twilio-accounts?locationId=${locationId}`,
        payload
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  updateNumberPool: async (locationId: string, numberPoolId: string, payload: any) => {
    try {
      const res = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/phone-system/number-pools/update?numberPoolId=${numberPoolId}&locationId=${locationId}`,
        payload
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  updateTwilioNumber: async (locationId: string, payload: any) => {
    const { phoneNumber, sid, isDefaultOutboundNumber } = payload

    try {
      const res = await requests(config.REVEX_BACKEND_LC_PHONE_URL).post(
        `numbers/location/${locationId}/add-twilio-number`,
        {
          number: phoneNumber,
          numberId: sid,
          defaultNumber: isDefaultOutboundNumber,
        }
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  checkAvailableNumbers: async (locationId: string, payload: any) => {
    try {
      const params = transformToNewPayload(payload);

      const queryString = new URLSearchParams({
        ...params,
        numberTypes: params.numberTypes.join(','),
        trackingNumbers: params.trackingNumbers.toString(),
        smsEnabled: params.smsEnabled.toString(),
        mmsEnabled: params.mmsEnabled.toString(),
        voiceEnabled: params.voiceEnabled.toString()
      }).toString();

      const url = `${config.REVEX_BACKEND_LC_PHONE_URL}/numbers/location/${locationId}/available?${queryString}`;
      const res = await requests(config.REVEX_BACKEND_LC_PHONE_URL).get(url)

      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  purchaseNumberPool: async (payload: any) => {
    try {
      const res = await requests(config.REVEX_BACKEND_LC_PHONE_URL).post('/number-pools/purchase', payload)
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getTollFreeVerificationStatus: async (companyId: string, locationId: string, tollFreePhoneNumberSID: string) => {
    try {
      const res = await requests(config.ISV).get(
        `tollFree/${companyId}/fetch/${locationId}?tollfreePhoneNumberSid=${tollFreePhoneNumberSID}`
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  createTollFreeVerificationRequest: async (companyId: string, locationId: string, payload: any) => {
    try {
      const res = await requests(config.ISV).post(`tollFree/${companyId}/create/${locationId}`, payload)
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  reSubmitTollFreeVerificationRequest: async (
    companyId: string,
    locationId: string,
    tollFreeVerificationSID: string,
    payload: any
  ) => {
    try {
      const res = await requests(config.ISV).post(
        `tollFree/${companyId}/resubmit/${locationId}?tollfreeVerificationSid=${tollFreeVerificationSID}`,
        payload
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getA2PStatus: async (companyId: string, locationId: string) => {
    try {
      const res = await requests(config.ISV).get(`/compliance/${companyId}/linked-numbers/${locationId}`)
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getTollFreeStatus: async (companyId: string, locationId: string) => {
    try {
      const res = await requests(config.ISV).get(`/tollFree/${companyId}/list/${locationId}`)
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  linkPhoneNumber: async (companyId: string, locationId: string, payload: any) => {
    try {
      const res = await requests(config.ISV).post(
        `/compliance/${companyId}/link-number-to-selected-messaging-service/${locationId}`,
        payload
      )
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  linkAddressOrBundleSid: async (payload: any) => {
    try {
      const res = await requests(config.SAAS).post(`regulatory-bundle/${payload.locationId}/link-number`, payload)
      return res
    } catch (error) {
      throw error
    }
  },
  getLocationPhoneNumbers: async (locationId: string) => {
    try {
      const res = await requests(config.MARKETPLACE_REST_API_URL).get(`/phone-system/numbers/location/${locationId}`)
      if (res.status === 200) {
        return Promise.resolve(res.data)
      } else {
        throw new Error(res.data.message)
      }
    } catch (error) {
      return Promise.reject(error)
    }
  },
  liftLocationSuspension: async (locationId: string) => {
    try {
      const res = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/phone-system/rate-limiter/unblock-location/${locationId}`
      )
      return res.data
    } catch (error) {
      throw error
    }
  },
  liftLocationTwilioSuspension: async payload => {
    try {
      const res = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/phone-system/twilio-accounts/lift-twilio-suspension`,
        payload
      )
      return res.data
    } catch (error) {
      throw error
    }
  },
  fetchShowUKKYCBanner: async (locationId: string) => {
    try {
      const res = await requests(config.REVEX_BACKEND_LC_PHONE_URL).get(`/sub-account/show-uk-kyc-banner/${locationId}`)
      if (res.status === 200) {
        return Promise.resolve(res.data)
      } else {
        throw new Error(res.data.message)
      }
    } catch (error) {
      throw error
    }
  },
  getLocationData: async (locationId: string) => {
    try {
      const res = await requests(config.MARKETPLACE_REST_API_URL).get(`/locations/${locationId}`)
      return res.data
    } catch (error) {
      throw error
    }
  },
  putLocationData: async (locationId: string, payload: any) => {
    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).put(`/locations/${locationId}`, { ...payload })
      return data
    } catch (error) {
      throw error
    }
  },
  fetchA2PRestriction: async (locationId: string) => {
    try {
      const res = await requests(config.REVEX_BACKEND_LC_PHONE_URL).get(
        `/sub-account/is-a2p-registration-restricted/${locationId}`
      )
      if (res.status === 200) {
        return res.data;
      } else {
        throw new Error(res.data.message)
      }
    } catch (error) {
      throw error;
    }
  },
  getInboundPhoneNumbersFromDB: async (locationId: string, payload: { limit: number, page: number, query?: string }) => {
    try {
      const res = await requests(config.REVEX_BACKEND_LC_PHONE_URL).get(
        `/numbers/v2/location/${locationId}?pageSize=${payload.limit}&page=${payload.page-1}&searchFilter=${payload.query}`
      )
      if (res.status === 200) {
        return Promise.resolve(res.data)
      } else {
        throw new Error(res.data.message)
      }
    } catch (error) {
      throw error;
    }
  },
  fetchLocationOnboardingConfig: async (companyId: string) => {
    try {
      const res = await requests(config.REVEX_BACKEND_LC_PHONE_URL).get(
        `/sub-account/onboarding-config/${companyId}`
      )
      if (res.status === 200) {
        return res.data;
      } else {
        throw new Error(res.data.message)
      }
    } catch (error) {
      throw error;
    }
  },
  updateOnboardingConfig: async (companyId: string, payload: any) => {
    try {
      const res = await requests(config.REVEX_BACKEND_LC_PHONE_URL).put(
        `/sub-account/onboarding-config/${companyId}`,
        payload
      )
      if (res.status === 200) {
        return res.data;
      } else {
        throw new Error(res.data.message)
      }
    } catch (error) {
      return {
        success: false,
        message: error?.response?.message
      }
    }
  }
}
